/**
 * 목록 묶어 보기 (2026-08-21) — 시간순 · 보낸사람.
 *
 * ★ 묶는 범위는 **지금 받아 온 것**이다. 서버가 묶어 주지 않으므로 화면이 가진 목록 안에서만
 *   묶인다 — "이지은 4통" 은 정확히는 "지금 목록에 있는 이지은 4통" 이다. 소비처가 그 사실을
 *   숨기지 않도록 groupMail 은 개수만 주고, 문구는 화면이 붙인다.
 *
 * ★ 시간 경계는 **로컬 달력일**로 끊는다. 24시간 단위로 빼면 오전에 열 때마다 어제 것이
 *   오늘로 올라온다(채팅 미디어 일자그룹에서 같은 함정을 겪었다).
 */

import type { MailListItem } from '../../api/mail';
import type { MailGroupBy } from './mailView';

export type MailDateBucket = 'today' | 'yesterday' | 'week' | 'older' | 'unknown';

export interface MailGroup {
  /** 묶음 식별자 — 접기 상태의 키로도 쓴다. */
  key: string;
  /** 시간 묶음이면 bucket, 보낸사람 묶음이면 null. */
  bucket: MailDateBucket | null;
  /** 화면에 그대로 적는 이름(보낸사람 묶음). 시간 묶음은 화면이 bucket 으로 번역한다. */
  label: string;
  items: MailListItem[];
}

/** 로컬 달력일 기준 0시. */
function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

export function dateBucketOf(sentAt: string | null, now: Date = new Date()): MailDateBucket {
  if (!sentAt) return 'unknown';
  const d = new Date(sentAt);
  if (Number.isNaN(d.getTime())) return 'unknown';
  const today = startOfDay(now);
  const day = startOfDay(d);
  const diffDays = Math.round((today - day) / 86_400_000);
  if (diffDays <= 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return 'week';
  return 'older';
}

const BUCKET_ORDER: MailDateBucket[] = ['today', 'yesterday', 'week', 'older', 'unknown'];

/**
 * 묶는다. `by === 'none'` 이면 빈 배열을 준다 — 소비처가 "묶지 않음" 을 그대로 그리게.
 *
 * 순서는 **들어온 순서를 지킨다**(정렬을 두 번 하지 않는다). 시간 묶음만 bucket 순서로 세운다.
 */
export function groupMail(
  items: MailListItem[],
  by: MailGroupBy,
  now: Date = new Date(),
): MailGroup[] {
  if (by === 'none' || items.length === 0) return [];

  const bag = new Map<string, MailGroup>();

  for (const m of items) {
    if (by === 'date') {
      const bucket = dateBucketOf(m.sentAt, now);
      const g = bag.get(bucket);
      if (g) g.items.push(m);
      else bag.set(bucket, { key: bucket, bucket, label: bucket, items: [m] });
    } else {
      // 밖에서 온 메일은 우리 사용자 id 가 없다 — 주소가 곧 사람이라 주소로 묶는다.
      const key = m.sender.isExternal || !m.sender.id ? `ext:${m.sender.email}` : `user:${m.sender.id}`;
      const g = bag.get(key);
      if (g) g.items.push(m);
      else bag.set(key, { key, bucket: null, label: m.sender.name || m.sender.email, items: [m] });
    }
  }

  const groups = [...bag.values()];
  if (by === 'date') {
    groups.sort((a, b) => BUCKET_ORDER.indexOf(a.bucket!) - BUCKET_ORDER.indexOf(b.bucket!));
  }
  return groups;
}
